import { useState, useEffect } from 'react';
import { Header } from './Header';
import { Hero } from './Hero';
import { USPRow } from './USPRow';
import { QuickQuoteStepper } from './QuickQuoteStepper';
import { BenefitsSection } from './BenefitsSection';
import { GalleryGrid } from './GalleryGrid';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { MaterialsOptions } from './MaterialsOptions';
import { ProcessTimeline } from './ProcessTimeline';
import { ReviewCarousel } from './ReviewCarousel';
import { FAQSection } from './FAQSection';
import { CTASection } from './CTASection';
import { TrustBadges } from './TrustBadges';
import { Footer } from './Footer';
import { StickyCallDock } from './StickyCallDock';
import { ServiceNav } from './ServiceNav';
import { SEO } from './SEO';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { ServiceData } from '../data/services';
import { galleryImages, heroImages, beforeAfterImages } from '../utils/galleryImages';

interface ServicePageProps {
  service: ServiceData;
  onNavigate: (slug: string) => void;
}

export function ServicePage({ service, onNavigate }: ServicePageProps) {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);

  // Exit intent detection
  useEffect(() => {
    let exitIntentShown = false;

    const handleMouseLeave = (e: MouseEvent) => {
      if (
        !exitIntentShown &&
        e.clientY <= 0 &&
        !quoteDialogOpen
      ) {
        exitIntentShown = true;
        setShowExitIntent(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [quoteDialogOpen]);

  // Scroll to gallery
  const scrollToGallery = () => {
    document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Prepare gallery items with images
  const galleryItems = service.galleryItems.map((item, index) => ({
    label: item.label,
    imageUrl: galleryImages[service.slug as keyof typeof galleryImages][index],
  }));

  // Before/After images from dedicated folder
  const beforeAfter = beforeAfterImages[service.slug as keyof typeof beforeAfterImages];

  // Get base URL for canonical and OG image
  const baseUrl = 'https://ads.ny-glass.com';
  const canonicalUrl = `${baseUrl}/#/services/${service.slug}`;
  const ogImage = heroImages[service.slug as keyof typeof heroImages];
  const fullOgImageUrl = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        ogImage={fullOgImageUrl}
        canonicalUrl={canonicalUrl}
      />

      {/* Header */}
      <Header onQuoteClick={() => setQuoteDialogOpen(true)} />

      {/* Hero */}
      <Hero
        h1={service.h1}
        subhead={service.heroSubhead}
        heroCopy={service.heroCopy}
        backgroundImage={heroImages[service.slug as keyof typeof heroImages]}
        onQuoteClick={() => setQuoteDialogOpen(true)}
        onGalleryClick={scrollToGallery}
      />

      {/* USP Row */}
      <USPRow />

      {/* Benefits */}
      <BenefitsSection benefits={service.benefits} />

      {/* Trust Badges */}
      <TrustBadges />

      {/* Before/After Slider */}
      <BeforeAfterSlider
        beforeImage={beforeAfter.before}
        afterImage={beforeAfter.after}
      />

      {/* Gallery */}
      <GalleryGrid items={galleryItems} />

      {/* Materials & Options */}
      <MaterialsOptions materials={service.materials} />

      {/* Process Timeline */}
      <ProcessTimeline />

      {/* Reviews */}
      <ReviewCarousel />

      {/* FAQ */}
      <FAQSection faqs={service.faqs} />

      {/* Related Services */}
      <ServiceNav currentSlug={service.slug} onNavigate={onNavigate} />

      {/* CTA */}
      <CTASection onQuoteClick={() => setQuoteDialogOpen(true)} />

      {/* Footer */}
      <Footer />

      {/* Sticky Call Dock (Mobile) */}
      <StickyCallDock />

      {/* Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
          <DialogTitle className="sr-only">Get Your Instant Quote</DialogTitle>
          <DialogDescription className="sr-only">
            Fill out the form to receive a custom quote for {service.h1}
          </DialogDescription>
          <QuickQuoteStepper
            serviceSlug={service.slug}
            serviceName={service.h1}
            onClose={() => setQuoteDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Exit Intent Popup */}
      <Dialog open={showExitIntent} onOpenChange={setShowExitIntent}>
        <DialogContent className="max-w-md">
          <DialogTitle>Wait! Special Offer</DialogTitle>
          <DialogDescription>
            Get a free upgrade to premium clear silicone when you request a quote in the next 24 hours.
          </DialogDescription>
          <div className="text-center p-6">
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowExitIntent(false);
                  setQuoteDialogOpen(true);
                }}
                className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Claim This Offer
              </button>
              <button
                onClick={() => setShowExitIntent(false)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                No thanks
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
